"use client";

import { useEffect, useState } from "react";
import { LANGS, LANG_META, LANG_COOKIE, t, type Lang } from "@/lib/i18n";
import { useLang } from "./LangProvider";

// Jeden przycisk ustawień (🌐) → jeden modal: Język · Waluta · Motyw.
// Odpowiednik belkowego setModal z landingu (public/landing.js), przeniesiony do
// panelu w React. Zapisy spójne z landingiem: język=cookie 'fablio_lang' (+reload),
// waluta=cookie 'cur' (bez reloadu — panel nie pokazuje cen; landing.js czyta cookie
// przy zamówieniu), motyw=klasa .night na <html> + localStorage 'fablio_theme'.

const CUR_COOKIE = "cur";
const CURRENCIES = [
  { code: "pln", flag: "🇵🇱", label: "PLN · zł" },
  { code: "eur", flag: "🇪🇺", label: "EUR · €" },
  { code: "usd", flag: "🇺🇸", label: "USD · $" },
  { code: "gbp", flag: "🇬🇧", label: "GBP · £" },
  { code: "sek", flag: "🇸🇪", label: "SEK · kr" },
  { code: "czk", flag: "🇨🇿", label: "CZK · Kč" },
] as const;
const SUPPORTED_CUR: readonly string[] = CURRENCIES.map((c) => c.code);

function getCookie(name: string): string | null {
  const m = document.cookie.match("(?:^|; )" + name + "=([^;]*)");
  return m ? decodeURIComponent(m[1]) : null;
}

// Zapis cookie poza komponentem (reguła react-hooks/immutability nie pozwala
// przypisywać document.cookie wewnątrz komponentu) — jak w AdminTiles/IdleTimeout.
function setCookie(name: string, value: string) {
  try {
    document.cookie = `${name}=${value};path=/;max-age=31536000;samesite=lax`;
  } catch {}
}

// Domyślna waluta gdy brak cookie — ta sama heurystyka co landing.js (detectCur).
function detectCur(): string {
  const tl = (navigator.language || "").toLowerCase();
  if (tl === "en-gb" || tl === "en-uk") return "gbp";
  const l = tl.split("-")[0];
  const map: Record<string, string> = {
    pl: "pln", en: "usd", de: "eur", fr: "eur", es: "eur",
    it: "eur", pt: "eur", nl: "eur", sv: "sek", cs: "czk",
  };
  return map[l] || "eur";
}

export default function SettingsMenu() {
  const lang = useLang();
  const [open, setOpen] = useState(false);
  const [night, setNight] = useState(true);
  const [cur, setCur] = useState("eur");

  // Stan początkowy z klienta (motyw z localStorage, waluta z cookie).
  useEffect(() => {
    let savedTheme: string | null = null;
    try {
      savedTheme = localStorage.getItem("fablio_theme");
    } catch {}
    const isNight = (savedTheme ?? "night") !== "day";
    // Odczyt stanu tylko-klienta (localStorage/cookie) na mount — celowe i bezpieczne dla SSR
    // (render domyślny, korekta po hydracji, bez mismatchu). To samo w setCur poniżej.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setNight(isNight);
    document.documentElement.classList.toggle("night", isNight);

    const c = getCookie(CUR_COOKIE);
    setCur(c && SUPPORTED_CUR.includes(c) ? c : detectCur());
  }, []);

  // Zamykanie klawiszem Esc.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  function pickLang(l: Lang) {
    setCookie(LANG_COOKIE, l);
    try {
      localStorage.setItem(LANG_COOKIE, l);
    } catch {}
    setOpen(false);
    window.location.reload(); // serwerowe komponenty renderują się w nowym języku
  }

  function pickCur(c: string) {
    if (!SUPPORTED_CUR.includes(c)) return;
    setCookie(CUR_COOKIE, c);
    setCur(c); // bez reloadu — modal zostaje otwarty, zaznaczenie się przesuwa
  }

  function pickTheme(mode: "day" | "night") {
    const isNight = mode === "night";
    setNight(isNight);
    document.documentElement.classList.toggle("night", isNight);
    try {
      localStorage.setItem("fablio_theme", isNight ? "night" : "day");
    } catch {}
  }

  const optionCls = (active: boolean) =>
    `flex w-full items-center gap-3 rounded-xl border-2 px-4 py-3 text-sm font-medium transition ${
      active ? "border-[var(--p-heading)]" : "border-transparent hover:border-[var(--p-heading)]"
    }`;
  const optionStyle = { background: "var(--p-soft)", color: "var(--p-text)" } as const;
  const headingCls = "mb-3 mt-6 text-center text-sm font-semibold text-[var(--p-heading)] first:mt-0";

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Język, waluta, motyw"
        title="Język · Waluta · Motyw"
        className="panel-navbtn"
      >
        <span className="text-lg leading-none">🌐</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-5"
          style={{ background: "rgba(20,16,48,.55)", backdropFilter: "blur(4px)" }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            className="relative max-h-[85vh] w-full max-w-md overflow-y-auto rounded-3xl border p-7 shadow-2xl"
            style={{
              background: "var(--p-card)",
              borderColor: "var(--p-card-border)",
              backdropFilter: "blur(20px)",
            }}
          >
            <button
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="absolute right-4 top-3 text-2xl leading-none text-[var(--p-muted)] hover:text-[var(--p-heading)]"
            >
              ×
            </button>

            {/* Język */}
            <h3 className={headingCls}>{t(lang, "nav.choose_lang")}</h3>
            <div className="grid grid-cols-2 gap-2">
              {LANGS.map((l) => (
                <button key={l} onClick={() => pickLang(l)} className={optionCls(l === lang)} style={optionStyle}>
                  <span className="text-xl">{LANG_META[l].flag}</span>
                  {LANG_META[l].name}
                </button>
              ))}
            </div>

            {/* Waluta */}
            <h3 className={headingCls}>Waluta · Currency</h3>
            <div className="grid grid-cols-2 gap-2">
              {CURRENCIES.map((c) => (
                <button key={c.code} onClick={() => pickCur(c.code)} className={optionCls(c.code === cur)} style={optionStyle}>
                  <span className="text-xl">{c.flag}</span>
                  {c.label}
                </button>
              ))}
            </div>

            {/* Motyw */}
            <h3 className={headingCls}>Motyw · Theme</h3>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => pickTheme("day")} className={optionCls(!night)} style={optionStyle}>
                <span className="text-xl">☀️</span>
                Jasny · Light
              </button>
              <button onClick={() => pickTheme("night")} className={optionCls(night)} style={optionStyle}>
                <span className="text-xl">🌙</span>
                Ciemny · Dark
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
